import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";

const REPLY_TEMPLATES = {
  broker: "Thank you for the introduction. We buy part of a position, not all of it, and we read the stock purchase agreement before any price is discussed. If your seller is open to that, tell us the company and the size and we will respond directly.",
  allocator: "Thank you for reaching out. We buy direct positions in named late stage companies and can move quickly once documents are signed. If you are selling down a direct position, share the company and the size and we will take it from there.",
  shareholder: "Thank you for writing. If you hold shares and are considering a partial sale, we will need to review your stock purchase agreement and obtain issuer confirmation before any price is discussed. Reply here and we will guide you through it.",
  general: "Thank you for reaching out to Solera. A member of the team will follow up with you directly.",
};

export function Messages() {
  const [msgs, setMsgs] = useState([]);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/contact-messages").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markRead = async (m) => {
    try { await api.post(`/admin/contact-messages/${m.contact_id}/read`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resolve = async (m) => {
    try { await api.post(`/admin/contact-messages/${m.contact_id}/resolve`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendReply = async (m) => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/contact-messages/${m.contact_id}/reply`, { message: replyText });
      toast.success("Reply sent");
      setReplyFor(null); setReplyText(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const catLabel = { broker: "Broker", allocator: "Allocator", shareholder: "Shareholder", other: "General" };
  return (
    <div className="max-w-3xl space-y-0" data-testid="admin-messages">
      {msgs.map((m) => (
        <div key={m.contact_id} className="hairline py-5" data-testid={`contact-message-${m.contact_id}`}>
          <div className="flex gap-6 items-baseline flex-wrap">
            <span className="t-body text-bone">{m.name}</span>
            <a href={`mailto:${m.email}`} className="t-cap text-muted-s hover:!text-[#E8E6E1]">{m.email}</a>
            {m.organization && <span className="t-cap text-muted-s">{m.organization}</span>}
            {m.category && <span className="t-label" data-testid={`contact-category-${m.contact_id}`}>{catLabel[m.category] || m.category}</span>}
            <span className="t-cap text-muted-s fig">{m.created_at?.slice(0, 16).replace("T", " ")} UTC</span>
            {!m.read && <span className="t-label">New</span>}
            {m.replied && <span className="t-label text-tint" data-testid={`replied-badge-${m.contact_id}`}>Replied</span>}
            {m.resolved && <span className="t-label text-tint" data-testid={`resolved-badge-${m.contact_id}`}>Resolved</span>}
          </div>
          <p className="t-small text-muted-s mt-2 measure">{m.message}</p>
          <div className="flex gap-5 mt-3">
            {!m.read && (
              <button onClick={() => markRead(m)} className="t-label hover:!text-[#E8E6E1]" data-testid={`mark-read-${m.contact_id}`}>Mark read</button>
            )}
            <button onClick={() => { setReplyFor(replyFor === m.contact_id ? null : m.contact_id); setReplyText(""); }} className="t-label hover:!text-[#E8E6E1]" data-testid={`reply-toggle-${m.contact_id}`}>{replyFor === m.contact_id ? "Cancel" : "Reply"}</button>
            {!m.resolved && (
              <button onClick={() => resolve(m)} className="t-label hover:!text-[#E8E6E1]" data-testid={`resolve-${m.contact_id}`}>Mark resolved</button>
            )}
          </div>
          {replyFor === m.contact_id && (
            <div className="mt-3 space-y-2">
              <div className="flex gap-2 flex-wrap" data-testid={`reply-templates-${m.contact_id}`}>
                {Object.entries(REPLY_TEMPLATES).map(([key, text]) => (
                  <button key={key} type="button" onClick={() => setReplyText(text)}
                    className="t-label px-3 py-1 border border-[#24272B] hover:!text-[#E8E6E1] capitalize"
                    data-testid={`reply-template-${key}-${m.contact_id}`}>{key}</button>
                ))}
              </div>
              <textarea className="input-solera" rows={4} placeholder={`Reply to ${m.name}. This is emailed to ${m.email}.`} value={replyText}
                onChange={(e) => setReplyText(e.target.value)} data-testid={`reply-text-${m.contact_id}`} />
              <button className="btn-primary" disabled={busy || !replyText.trim()} onClick={() => sendReply(m)} data-testid={`reply-send-${m.contact_id}`}>
                {busy ? "Sending" : "Send reply"}
              </button>
            </div>
          )}
        </div>
      ))}
      {msgs.length === 0 && <p className="t-small text-muted-s">No messages yet.</p>}
    </div>
  );
}
